import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, ShoppingCart, Code2, MessageSquare, Server,
  ChevronRight, GitBranch, CheckCircle, Copy, HelpCircle,
  UserPlus, Link2, Bot, Settings, Target
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsGettingStartedDecisionTree() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const [answers, setAnswers] = useState({});

  const setAnswer = (question, value) => {
    setAnswers(prev => ({ ...prev, [question]: value }));
  };

  const journeySteps = [
    { number: 1, title: 'Create Account', desc: 'Sign up via Magic Link, Google OAuth, or Shopify App Store install.', icon: UserPlus },
    { number: 2, title: 'Connect Platform', desc: 'Link your Shopify store or import products via CSV/API.', icon: Link2 },
    { number: 3, title: 'Enable Agents', desc: 'Activate department heads under your AI CEO (start with 3+) and configure their guardrails.', icon: Bot },
    { number: 4, title: 'Set Up Messaging', desc: 'Connect OpenClaw to Telegram, WhatsApp, or Slack for messaging-first management.', icon: MessageSquare },
    { number: 5, title: 'Configure Goals', desc: 'Set revenue targets, inventory thresholds, and CX benchmarks for your agents.', icon: Target },
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Getting Started
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Choose Your Setup Path
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Answer a few questions and we'll point you to the right guide. Commerce Index works with Shopify, custom platforms, and everything in between.
        </p>
      </div>

      {/* Decision Tree */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Interactive Decision Tree
        </h2>

        {/* Question 1: Shopify? */}
        <Card className="bg-[#0D1628] border-[#22304A] mb-4">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#3EF1C7]/10">
                <HelpCircle className="w-4 h-4 text-[#3EF1C7]" />
              </div>
              <CardTitle className="text-white">Do you use Shopify?</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button
                onClick={() => setAnswer('shopify', 'yes')}
                className={answers.shopify === 'yes'
                  ? 'bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90'
                  : 'bg-[#1E293B] text-[#E2E8F0] hover:bg-[#334155] border border-[#334155]'}
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> Yes, I use Shopify
              </Button>
              <Button
                onClick={() => setAnswer('shopify', 'no')}
                className={answers.shopify === 'no'
                  ? 'bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90'
                  : 'bg-[#1E293B] text-[#E2E8F0] hover:bg-[#334155] border border-[#334155]'}
              >
                <Code2 className="w-4 h-4 mr-2" /> No, I use something else
              </Button>
            </div>

            {answers.shopify === 'yes' && (
              <div className="mt-4 p-4 bg-[#0B1120] border border-[#3EF1C7]/30 rounded-lg">
                <p className="text-[#E2E8F0] mb-3">
                  Great! Install Commerce Index from the Shopify App Store for automatic product sync, webhook integration, and one-click agent setup.
                </p>
                <Link to="/docs/shopify-setup">
                  <Button className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90">
                    Go to Shopify Setup Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}

            {answers.shopify === 'no' && (
              <div className="mt-4 p-4 bg-[#0B1120] border border-[#7A5CFF]/30 rounded-lg">
                <p className="text-[#E2E8F0] mb-3">
                  No problem! You can import products via CSV or use our REST API. Works with WooCommerce, BigCommerce, Magento, custom platforms, and more.
                </p>
                <Link to="/docs/manual-setup">
                  <Button className="bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90">
                    Go to Manual Setup Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Question 2: Messaging? */}
        <Card className="bg-[#0D1628] border-[#22304A] mb-4">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#FFD166]/10">
                <HelpCircle className="w-4 h-4 text-[#FFD166]" />
              </div>
              <CardTitle className="text-white">Want messaging-first management?</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-4">
              OpenClaw lets you manage your entire store from Telegram, WhatsApp, Slack, or iMessage. Run commands, get alerts, and approve agent decisions — all from your messaging app.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setAnswer('messaging', 'yes')}
                className={answers.messaging === 'yes'
                  ? 'bg-[#FFD166] text-[#0B1120] hover:bg-[#FFD166]/90'
                  : 'bg-[#1E293B] text-[#E2E8F0] hover:bg-[#334155] border border-[#334155]'}
              >
                <MessageSquare className="w-4 h-4 mr-2" /> Yes, set up messaging
              </Button>
              <Button
                onClick={() => setAnswer('messaging', 'no')}
                className={answers.messaging === 'no'
                  ? 'bg-[#334155] text-[#E2E8F0]'
                  : 'bg-[#1E293B] text-[#E2E8F0] hover:bg-[#334155] border border-[#334155]'}
              >
                Maybe later
              </Button>
            </div>

            {answers.messaging === 'yes' && (
              <div className="mt-4 p-4 bg-[#0B1120] border border-[#FFD166]/30 rounded-lg">
                <p className="text-[#E2E8F0] mb-3">
                  OpenClaw supports 30+ commands across all major messaging platforms. Set it up in under 5 minutes.
                </p>
                <Link to="/docs/openclaw">
                  <Button className="bg-[#FFD166] text-[#0B1120] hover:bg-[#FFD166]/90">
                    Go to OpenClaw Setup <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Question 3: Self-host? */}
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#FF6B8A]/10">
                <HelpCircle className="w-4 h-4 text-[#FF6B8A]" />
              </div>
              <CardTitle className="text-white">Want to self-host?</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-4">
              Run Commerce Index on your own hardware for maximum control and data sovereignty. Our Mac Mini guide covers local deployment with Ollama for on-device LLM inference.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setAnswer('selfhost', 'yes')}
                className={answers.selfhost === 'yes'
                  ? 'bg-[#FF6B8A] text-white hover:bg-[#FF6B8A]/90'
                  : 'bg-[#1E293B] text-[#E2E8F0] hover:bg-[#334155] border border-[#334155]'}
              >
                <Server className="w-4 h-4 mr-2" /> Yes, I want to self-host
              </Button>
              <Button
                onClick={() => setAnswer('selfhost', 'no')}
                className={answers.selfhost === 'no'
                  ? 'bg-[#334155] text-[#E2E8F0]'
                  : 'bg-[#1E293B] text-[#E2E8F0] hover:bg-[#334155] border border-[#334155]'}
              >
                No, use hosted version
              </Button>
            </div>

            {answers.selfhost === 'yes' && (
              <div className="mt-4 p-4 bg-[#0B1120] border border-[#FF6B8A]/30 rounded-lg">
                <p className="text-[#E2E8F0] mb-3">
                  Our Mac Mini deployment guide walks you through installing Commerce Index, Ollama, and OpenClaw on your own hardware.
                </p>
                <Link to="/docs/mac-mini">
                  <Button className="bg-[#FF6B8A] text-white hover:bg-[#FF6B8A]/90">
                    Go to Mac Mini Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Recommended Path */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Recommended Path
        </h2>
        <p className="text-[#94A3B8] mb-6">
          Regardless of your platform, here's the 5-step journey to get fully set up with Commerce Index:
        </p>
        <div className="space-y-0">
          {journeySteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.number} className="relative">
                {index < journeySteps.length - 1 && (
                  <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-[#334155]" />
                )}
                <div className="flex gap-6 pb-8">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#3EF1C7] flex items-center justify-center text-[#0B1120] font-bold relative z-10">
                    {step.number}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-5 h-5 text-[#3EF1C7]" />
                      <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-[#94A3B8]">{step.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* What's Next */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What's Next?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <ul className="space-y-3">
              {[
                { text: 'Shopify Setup Guide', link: '/docs/shopify-setup' },
                { text: 'Manual / Non-Shopify Setup', link: '/docs/manual-setup' },
                { text: 'OpenClaw Messaging Setup', link: '/docs/openclaw' },
                { text: 'Meet the agent org chart', link: '/docs/agents' },
                { text: 'Onboarding Checklist', link: '/docs/onboarding-checklist' },
              ].map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="flex items-center gap-2 text-[#3EF1C7] hover:underline">
                    <ChevronRight className="w-4 h-4" />
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
